<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';
const unSortedAnnotations = __MACROS_toRef(__props, "annotations");

import { toRef as __MACROS_toRef } from "vue";
import { computed, inject, onMounted, ref } from "vue";

import RsLoading from "@/components/Loading.vue";
import RsViewerAnnotation from "./Annotation/ViewerAnnotation.vue";
import ViewerAnnotationArea from "./Annotation/ViewerAnnotationArea.vue";
import RsViewerAnnotationCreate from "./Annotation/ViewerAnnotationCreate.vue";
import RsViewerAnnotationHighlight from "./Annotation/ViewerAnnotationHighlight.vue";
import RsViewerAnnotationHighlightCreate from "./Annotation/ViewerAnnotationHighlightCreate.vue";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { isHighlightAnnotation, isRectangleAnnotation } from "@/services/UtilitiesService";

import type { Ref } from "vue";
import type { MouseTool } from "./Viewer.vue";
import type { ParsedMark } from "@/models/Mark";
import type { HighlightCustomArea, SearchResult, TextLine } from "@/models/Viewer";
import type { HighlightAnnotation, RectangleAnnotation } from "@/models/Annotation";

const emit = defineEmits<{
  (e: "selectPage"): void;
  (e: "loadPage"): void;
  (e: "dragEnd"): void;
  (e: "reloadAnnotations"): void;
}>();
withDefaults(defineProps<{
  session: string;
  viewerScale: { width: number; height: number };
  pageNumber: number;
  fileReference: string;
  svg: string;
  textLines: TextLine[];
  dimensions: { width: number; height: number };
  annotations: Array<ParsedMark<HighlightAnnotation | RectangleAnnotation>>;
  searchResults: SearchResult[];
  selectedSearch: SearchResult | undefined;
  pageScale: number;
  currentMouseTool: MouseTool;
  pageStatus: "INIT" | "LOADING" | "SUCCESS" | "ERROR";
  selectedArea: HighlightCustomArea | undefined;
}>(), {  })

const routeParams = useCaseRouteParams();

const pageRef = ref<SVGSVGElement | null>(null);
const svgRef = ref<SVGSVGElement | null>(null);
const rotation = inject<Ref<number[]>>("rotation")!;

let mousePosition = _ref<{ x: number; y: number } | null>(null);
const searchOffset = _ref(0);

const pageStyle = _computed(() => {
  if (rotation.value[__props.pageNumber] && [90, 270].includes(Math.abs(rotation.value[__props.pageNumber]))) {
    return {
      width: __props.dimensions.height * __props.pageScale + "px",
      height: __props.dimensions.width * __props.pageScale + "px",
    };
  }

  return {
    width: __props.dimensions.width * __props.pageScale + "px",
    height: __props.dimensions.height * __props.pageScale + "px",
  };
});

const sortedAnnotations = computed(() => {
  const result = __props.annotations.flatMap((annotation) => {
    try {
      if (isRectangleAnnotation(annotation.parsedMarkData)) {
        return {
          generalType: "RectangleAnnotation" as const,
          annotation: annotation as ParsedMark<RectangleAnnotation>,
        };
      }

      if (isHighlightAnnotation(annotation.parsedMarkData)) {
        return {
          generalType: "HighlightAnnotation" as const,
          annotation: annotation as ParsedMark<HighlightAnnotation>,
        };
      }

      return [];
    } catch (error) {
      return [];
    }
  });
  if (routeParams.value.annotation == null) return result;

  return result.sort((a, b) => {
    // If `a` is the selected, move `a` to end
    if (a.annotation.markId === routeParams.value.annotation) return 1;
    // If `b` is the selected, move `a` to start
    if (b.annotation.markId === routeParams.value.annotation) return -1;
    // Neither are selected, can stay
    return 0;
  });
});

const transform = _computed(() => {
  switch (rotation.value[__props.pageNumber]) {
    case 0:
      return `scale(${__props.pageScale})`;
    case 90:
      return `scale(${__props.pageScale}) translate(0 ${__props.dimensions.width}) rotate(270)`;
    case 180:
      return `scale(${__props.pageScale}) translate(${__props.dimensions.width} ${__props.dimensions.height}) rotate(180)`;
    case 270:
      return `scale(${__props.pageScale}) translate(${__props.dimensions.height}) rotate(90)`;
    default:
      return `scale(${__props.pageScale})`;
  }
});

onMounted(() => {
  if (__props.pageStatus !== "INIT") return;
  loadPage();
});

function loadPage() {
  emit("loadPage");
}

function getMousePosition(evt: MouseEvent) {
  if (!pageRef.value || !svgRef.value) return mousePosition.value;

  // https://stackoverflow.com/questions/4850821/svg-coordinates-with-transform-matrix
  const svgPoint = svgRef.value.createSVGPoint();
  const page = pageRef.value;
  svgPoint.x = evt.clientX;
  svgPoint.y = evt.clientY;
  const mp = svgPoint.matrixTransform(page.getScreenCTM()?.inverse());
  return { x: mp.x, y: mp.y };
}

function drag(evt: MouseEvent) {
  mousePosition.value = getMousePosition(evt);
}
</script>
<template>
  <div
    class="tw-relative tw-mx-auto tw-my-3 tw-shadow"
    :style="pageStyle"
    :class="{ 'tw-overflow-hidden': currentMouseTool === 'THUMBNAILS' }"
    @mousemove="drag"
  >
    <RsLoading
      v-if="pageStatus === 'LOADING' || pageStatus === 'INIT'"
      :id="`page-${pageNumber}-loading`"
      :message="`Loading page ${pageNumber}`"
    />
    <div
      v-else-if="pageStatus === 'ERROR'"
      class="tw-sticky tw-top-0 tw-mx-auto tw-flex tw-max-w-xl tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-4 tw-text-lg tw-tracking-wide"
    >
      <p class="tw-text-lg tw-tracking-wide">
        Error loading this
        {{ currentMouseTool === "THUMBNAILS" ? "thumbnail" : "page" }} ({{ pageNumber }})
      </p>
      <h3 class="tw-text-xl">
        You can retry the page manually by clicking
        <button type="button" class="tw-font-bold" @click.prevent="loadPage">here</button>.
      </h3>
    </div>
    <svg
      v-else-if="pageStatus === 'SUCCESS'"
      ref="svgRef"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      xlink="http://www.w3.org/1999/xlink"
      image-rendering="optimizeQuality"
    >
      <g v-if="currentMouseTool === 'THUMBNAILS'" :transform="transform">
        <image
          x="0"
          y="0"
          opacity="1"
          display="block"
          externalResourcesRequired="true"
          :href="svg"
          :width="dimensions.width"
          :height="dimensions.height"
        ></image>
      </g>
      <g
        v-else
        ref="pageRef"
        class="disable-drag tw-cursor-pointer"
        :transform="transform"
        @click="$emit('selectPage')"
        v-html="svg"
      />
      <g v-for="sr in searchResults" :key="sr.id" :transform="transform" fill-opacity="1">
        <g
          :transform="`scale(${
            [90, 270].includes(rotation[pageNumber])
              ? dimensions.height / (sr.pageData.height - searchOffset)
              : dimensions.width / (sr.pageData.width - searchOffset)
          })`"
          fill-opacity="1"
        >
          <svg
            :height="sr.pageData.height - searchOffset"
            :width="sr.pageData.width - searchOffset"
            :viewBox="`0 0 ${sr.pageData.width - searchOffset} ${sr.pageData.height - searchOffset}`"
          >
            <rect
              :x="sr.boundingRectangle.x"
              :y="sr.boundingRectangle.y"
              :width="sr.boundingRectangle.width"
              :height="sr.boundingRectangle.height"
              :style="`fill: ${selectedSearch && sr.id === selectedSearch.id ? 'green' : 'blue'}`"
              class="tw-cursor-move tw-opacity-25"
            />
          </svg>
        </g>
      </g>
      <g v-if="sortedAnnotations.length > 0" fill-opacity="1" :transform="transform">
        <template v-for="({ generalType, annotation }, idx) in sortedAnnotations" :key="`${annotation.markId}-${idx}`">
          <RsViewerAnnotation
            v-if="generalType === 'RectangleAnnotation'"
            :annotation="annotation"
            :page-scale="pageScale"
            :page-dimensions="dimensions"
            :page-number="pageNumber"
            :session="session"
            :mouse-position="mousePosition"
            :is-display="currentMouseTool === 'THUMBNAILS'"
            @select-page="$emit('selectPage')"
            @reload-annotations="() => $emit('reloadAnnotations')"
          />
          <RsViewerAnnotationHighlight
            v-else-if="generalType === 'HighlightAnnotation'"
            :annotation="annotation"
            :page-scale="pageScale"
            :page-dimensions="dimensions"
            :page-number="pageNumber"
            :session="session"
            :mouse-position="mousePosition"
            :is-display="currentMouseTool === 'THUMBNAILS'"
            :text-lines="textLines"
            @select-page="$emit('selectPage')"
            @reload-annotations="$emit('reloadAnnotations')"
          />
        </template>
      </g>
      <g
        v-if="currentMouseTool === 'RectangleAnnotation' || currentMouseTool === 'RectangleRedaction'"
        fill-opacity="1"
        :transform="transform"
      >
        <RsViewerAnnotationCreate
          :file-reference="fileReference"
          :page-ref="pageRef"
          :page-scale="pageScale"
          :page-dimensions="dimensions"
          :page-number="pageNumber"
          :session="session"
          :mouse-position="mousePosition"
          :type="currentMouseTool"
          @drag-end="$emit('dragEnd')"
          @reload-annotations="$emit('reloadAnnotations')"
        />
      </g>
      <g
        v-else-if="
          currentMouseTool === 'HighlightAnnotation' ||
          currentMouseTool === 'TextSelectionRedaction' ||
          currentMouseTool === 'SelectText'
        "
        fill-opacity="1"
        :transform="transform"
      >
        <RsViewerAnnotationHighlightCreate
          :key="currentMouseTool"
          :page-scale="pageScale"
          :page-dimensions="dimensions"
          :page-number="pageNumber"
          :session="session"
          :mouse-position="mousePosition"
          :text-lines="textLines"
          :file-reference="fileReference"
          :type="currentMouseTool"
          @select-page="$emit('selectPage')"
          @drag-end="$emit('dragEnd')"
          @reload-annotations="$emit('reloadAnnotations')"
        />
      </g>
      <g v-else-if="currentMouseTool === 'THUMBNAILS'" :transform="transform">
        <rect
          :x="0"
          :y="0"
          :width="dimensions.width"
          :height="dimensions.height"
          :style="`opacity: 0; cursor: pointer;`"
          @click="$emit('selectPage')"
        />
      </g>
      <g v-if="selectedArea != null" :transform="transform" fill-opacity="1">
        <g
          :transform="`scale(${
            [90, 270].includes(rotation[pageNumber])
              ? dimensions.height / selectedArea.page.height
              : dimensions.width / selectedArea.page.width
          })`"
          fill-opacity="1"
        >
          <ViewerAnnotationArea :="selectedArea" />
        </g>
      </g>
    </svg>
  </div>
</template>
<style lang="scss" scoped>
.disable-drag {
  user-drag: none;
  -webkit-user-drag: none;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}
</style>
